const { isEmptyOrNotSet, findByAddress } = require('@hints/utils/data');
const { round, ratio } = require('@hints/utils/math');

const CM_PER_INCH = 2.54;
const INCHES_PER_FOOT = 12;

const UNIT_SETTINGS = {
    weight: {
        si: {
            ratioToSystem: 1,
            unitSuffix: 'kg'
        }, 
        imperial: {
            ratioToSystem: 2.20462,
            unitSuffix: 'lb'
        },
    },
    height: {
        si: {
            ratioToSystem: 1,
            unitSuffix: 'cm'
        },
        imperial: {
            // Handled as feet'inches (see _getFeetInchesFromValue / _getValueFromFeetInches)
            unitSuffix: 'ft'
        }
    }
};

/** Converts a value in the given system to the stored value (cm / kg) */
function getValueFromSystem(type, value, system) {
    if(isEmptyOrNotSet(value)) return undefined;
    if(_isFeetInches(type, system)) return _getValueFromFeetInches(value);
    const ratioValueToSystem = _getSystemRatio(type, system);
    const ratioValueFromSystem = ratio(1, ratioValueToSystem);
    return round(value * ratioValueFromSystem, 0);
} 

/** Converts a stored value (cm / kg) to the given system. Imperial height is a feet'inches string, eg: 180 => 5'11 */
function getValueInSystem(type, value, system) {
    if(isEmptyOrNotSet(value)) return undefined;
    if(_isFeetInches(type, system)) return _getFeetInchesFromValue(value);
    const ratioValueToSystem = _getSystemRatio(type, system);
    return round(value * ratioValueToSystem, 0);
}

function formatValueInSystem(type, value, system) {
    if(isEmptyOrNotSet(value)) return undefined;
    if(_isFeetInches(type, system)) {
        const totalInches = _getTotalInches(value);
        return `${_getFeet(totalInches)}'${_getInches(totalInches)}"`;
    }
    const systemUnit = _getSystemSuffix(type, system);
    const valueInSystem = getValueInSystem(type, value, system);
    return `${valueInSystem} ${systemUnit}`;
}

module.exports = {
    getValueFromSystem,
    getValueInSystem,
    formatValueInSystem,
};

/** 180 (cm) => 5'11 */
function _getFeetInchesFromValue(value) {
    const totalInches = _getTotalInches(value);
    return `${_getFeet(totalInches)}'${_getInches(totalInches)}`;
}

/** 5'11 or 5'11" => 180 (cm). Inches are not capped: 5'12 is read as 6 feet */
function _getValueFromFeetInches(feetInches) {
    const [feet, inches] = String(feetInches).replace(/"/g, '').split('\'');
    const totalInches = (Number(feet) || 0) * INCHES_PER_FOOT + (Number(inches) || 0);
    return round(totalInches * CM_PER_INCH, 0);
}

function _getTotalInches(value) { return Math.round(value / CM_PER_INCH); }
function _getFeet(totalInches) { return Math.floor(totalInches / INCHES_PER_FOOT); }
function _getInches(totalInches) { return totalInches % INCHES_PER_FOOT; }

function _isFeetInches(type, system) {
    return type === 'height' && system === 'imperial';
}

function _getSystemRatio(type, system) {
    if(isEmptyOrNotSet(system)) system = 'si';
    if(isEmptyOrNotSet(type)) return 1;
    return findByAddress(UNIT_SETTINGS, `${type}.${system}.ratioToSystem`, 1);
}

function _getSystemSuffix(type, system) {
    if(isEmptyOrNotSet(system)) system = 'si';
    if(isEmptyOrNotSet(type)) return 1;
    return findByAddress(UNIT_SETTINGS, `${type}.${system}.unitSuffix`, 1);
}
